import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDate } from '../lib/util';
import { DeadlineIndicator } from '../components/DeadlineIndicator';
import { SearchFilterBar, useSearchFilter } from '../components/SearchFilterBar';
import { STRIKE_THRESHOLDS } from '../domain/constants';
import { useAppData } from '../state/contexts/AppDataContext';
import { useUIState } from '../state/contexts/UIStateContext';
import { useDisputeActions } from '../state/actions/disputes';

export function IpDisputes() {
  const { cases, now } = useAppData();
  const { setModal, setModalData } = useUIState();
  const {
    notifyRegistrantShaft, resolveShaftDispute,
    notifyRegistrantIp, suspendIpContent, resolveIpDispute,
  } = useDisputeActions();

  const allShaft = cases.flatMap(c => (c.shaftDisputes || []).map((d: any) => ({ ...d, caseId: c.id, registrant: c.registrant })));
  const allIp = cases.flatMap(c => (c.ipDisputes || []).map((d: any) => ({ ...d, caseId: c.id, registrant: c.registrant })));
  const activeShaft = allShaft.filter((d: any) => !['APPROVED', 'DENIED', 'DISMISSED'].includes(d.status));
  const activeIp = allIp.filter((d: any) => !['REINSTATED', 'TERMINATED', 'REMOVED', 'RESOLVED'].includes(d.status));
  const allStrikes = cases.flatMap(c => (c.infringerStrikes || []).map((s: any) => ({ ...s, caseId: c.id, registrant: c.registrant })));

  // Search + facets over both dispute tables at once: rows are tagged with
  // their kind so a single bar (text query + kind + status) drives both. The
  // Repeat-Infringer strike table below is left unfiltered — it's a distinct
  // audit view, not part of the dispute queue.
  const disputes = [
    ...allShaft.map((d: any) => ({ ...d, kind: 'SHAFT' as const })),
    ...allIp.map((d: any) => ({ ...d, kind: 'IP' as const })),
  ];
  const { filtered: filteredDisputes, bar } = useSearchFilter(
    disputes,
    (d) => [d.caseId, d.registrant, d.contentType, d.type, d.claimantName, d.status].filter(Boolean).join(' '),
    [
      {
        id: 'kind',
        label: 'Dispute type',
        options: [
          { value: '', label: 'All disputes' },
          { value: 'SHAFT', label: 'SHAFT content' },
          { value: 'IP', label: 'Copyright / Trademark' },
        ],
        predicate: (d, v) => d.kind === v,
      },
    ],
    { placeholder: 'Search disputes by case, registrant, claimant, type, or status…' },
  );
  const shaft = filteredDisputes.filter((d) => d.kind === 'SHAFT');
  const ip = filteredDisputes.filter((d) => d.kind === 'IP');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text }}>IP & SHAFT Disputes</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...styles.btn, ...styles.btnGold }} onClick={() => setModal('new-shaft-dispute')}>+ SHAFT Dispute</button>
          <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={() => setModal('new-ip-dispute')}>+ IP Claim</button>
        </div>
      </div>
      <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 20 }}>SHAFT content determinations · Copyright/Trademark claims · DMCA takedowns · Repeat Infringer tracking</div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.warn}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.warn }}>{activeShaft.length}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Active SHAFT Disputes</div>
        </div>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.danger}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.danger }}>{activeIp.length}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Active IP Claims</div>
        </div>
        <div style={{ ...styles.statBox, borderLeft: `3px solid ${C.info}` }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.info }}>{allStrikes.length}</div>
          <div style={{ fontSize: 11, color: C.textMuted }}>Infringer Strikes (12mo)</div>
        </div>
      </div>
      <SearchFilterBar {...bar} />
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: C.warn, marginBottom: 12 }}>SHAFT Content Disputes</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>OA notifies registrant within 10 biz days · Registrant has 90 calendar days to modify or object · BCID LLC reviews objections within 30 calendar days</div>
        {shaft.length > 0 ? (
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Case</th><th style={styles.th}>Content</th><th style={styles.th}>Status</th><th style={styles.th}>Flagged</th><th style={styles.th}>Deadline</th><th style={styles.th}>Actions</th></tr></thead>
            <tbody>
              {shaft.map((d: any) => (
                <tr key={d.id}>
                  <td style={styles.td}><span style={{ color: C.goldText, fontWeight: 600 }}>{d.caseId}</span> <span style={{ fontSize: 11, color: C.textMuted }}>{d.registrant}</span></td>
                  <td style={styles.td}><span style={{ ...styles.tag, background: C.warnBg, color: C.warn }}>{d.contentType}</span></td>
                  <td style={styles.td}><span style={{ ...styles.badge, background: d.status === 'FLAGGED' ? C.dangerBg : d.status === 'APPROVED' ? C.successBg : C.warnBg, color: d.status === 'FLAGGED' ? C.danger : d.status === 'APPROVED' ? C.success : C.warn }}>{d.status}</span></td>
                  <td style={styles.td}>{fmtDate(d.flaggedAt)}</td>
                  <td style={styles.td}>
                    {d.status === 'FLAGGED' && <DeadlineIndicator deadline={d.oaNotifyDeadline} label="OA notify (10 biz days)" now={now} />}
                    {d.status === 'NOTIFIED' && <DeadlineIndicator deadline={d.registrantResponseDeadline} label="Registrant response (90 cal days)" now={now} />}
                    {d.status === 'OBJECTION_SUBMITTED' && <DeadlineIndicator deadline={d.bcidReviewDeadline} label="BCID LLC review (30 cal days)" now={now} />}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {d.status === 'FLAGGED' && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => notifyRegistrantShaft(d.caseId, d.id)}>Notify Registrant</button>}
                      {d.status === 'NOTIFIED' && <>
                        <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { setModal('shaft-response'); setModalData({ caseId: d.caseId, disputeId: d.id, responseType: 'MODIFICATION' }); }}>Log Modification</button>
                        <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { setModal('shaft-response'); setModalData({ caseId: d.caseId, disputeId: d.id, responseType: 'OBJECTION' }); }}>Log Objection</button>
                      </>}
                      {d.status === 'MODIFICATION_SUBMITTED' && <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => resolveShaftDispute(d.caseId, d.id, 'APPROVED')}>Approve Modification</button>}
                      {d.status === 'OBJECTION_SUBMITTED' && <>
                        <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => resolveShaftDispute(d.caseId, d.id, 'APPROVED')}>BCID Approves</button>
                        <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => resolveShaftDispute(d.caseId, d.id, 'DENIED')}>BCID Denies (Final)</button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div style={{ color: C.textMuted, fontSize: 13 }}>{allShaft.length === 0 ? 'No SHAFT disputes' : 'No SHAFT disputes match the current filters'}</div>}
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: C.danger, marginBottom: 12 }}>Copyright & Trademark Claims</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>24hr takedown · 7-day registrant response · Counter-claim process · Settlement Platform notification within 24hrs of resolution</div>
        {ip.length > 0 ? (
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Case</th><th style={styles.th}>Type</th><th style={styles.th}>Claimant</th><th style={styles.th}>Status</th><th style={styles.th}>Received</th><th style={styles.th}>Deadline</th><th style={styles.th}>Actions</th></tr></thead>
            <tbody>
              {ip.map((d: any) => (
                <tr key={d.id}>
                  <td style={styles.td}><span style={{ color: C.goldText, fontWeight: 600 }}>{d.caseId}</span> <span style={{ fontSize: 11, color: C.textMuted }}>{d.registrant}</span></td>
                  <td style={styles.td}><span style={{ ...styles.tag, background: d.type === 'COPYRIGHT' ? C.infoBg : 'rgba(128,90,213,0.08)', color: d.type === 'COPYRIGHT' ? C.info : '#805AD5' }}>{d.type}</span></td>
                  <td style={styles.td}>{d.claimantName}</td>
                  <td style={styles.td}><span style={{ ...styles.badge, background: d.status === 'RECEIVED' ? C.dangerBg : d.status === 'SUSPENDED' ? C.warnBg : d.status === 'REINSTATED' ? C.successBg : C.infoBg, color: d.status === 'RECEIVED' ? C.danger : d.status === 'SUSPENDED' ? C.warn : d.status === 'REINSTATED' ? C.success : C.info }}>{d.status.replace(/_/g, ' ')}</span></td>
                  <td style={styles.td}>{fmtDate(d.receivedAt)}</td>
                  <td style={styles.td}>
                    {d.status === 'RECEIVED' && <DeadlineIndicator deadline={d.notifyDeadline} label="24hr notify" now={now} />}
                    {d.status === 'REGISTRANT_NOTIFIED' && <DeadlineIndicator deadline={d.registrantResponseDeadline} label="7-day response" now={now} />}
                    {d.status === 'CONTESTED' && d.type === 'COPYRIGHT' && <DeadlineIndicator deadline={d.reinstateLatestDate} label="Reinstate by" now={now} />}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {d.status === 'RECEIVED' && <>
                        <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={() => notifyRegistrantIp(d.caseId, d.id)}>Notify Registrant</button>
                        <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => suspendIpContent(d.caseId, d.id)}>Suspend Content</button>
                      </>}
                      {(d.status === 'REGISTRANT_NOTIFIED' || d.status === 'SUSPENDED') && <>
                        <button style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }} onClick={() => { setModal('ip-counter-claim'); setModalData({ caseId: d.caseId, disputeId: d.id }); }}>Log Counter-Claim</button>
                        <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => resolveIpDispute(d.caseId, d.id, 'TERMINATED')}>No Response → Terminate</button>
                      </>}
                      {d.status === 'CONTESTED' && <>
                        <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={() => resolveIpDispute(d.caseId, d.id, 'REINSTATED')}>Reinstate Content</button>
                        <button style={{ ...styles.btn, ...styles.btnDanger, ...styles.btnSmall }} onClick={() => resolveIpDispute(d.caseId, d.id, 'REMOVED')}>Remove Permanently</button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div style={{ color: C.textMuted, fontSize: 13 }}>{allIp.length === 0 ? 'No IP claims filed' : 'No IP claims match the current filters'}</div>}
      </div>
      <div style={styles.card}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Repeat Infringer Policy</h3>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>Rolling 12-month window · Strike 1: Warning + 48hr remediation · Strike 2: 7-day suspension + full audit · Strike 3: Permanent termination + ITG reporting</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          {[
            { label: '3 Valid Claims', desc: '3 verified IP claims not successfully contested', threshold: STRIKE_THRESHOLDS.VALID_CLAIMS, color: C.danger },
            { label: '2 Material Misrepresentations', desc: 'False LOAs or business docs on 2+ occasions', threshold: STRIKE_THRESHOLDS.MISREPRESENTATIONS, color: C.warn },
            { label: '1 Fraudulent Intent', desc: 'Brand spoofing, phishing, or govt agency impersonation', threshold: STRIKE_THRESHOLDS.FRAUDULENT_INTENT, color: C.danger },
          ].map((t, i) => (
            <div key={i} style={{ background: C.bgInput, borderRadius: 8, padding: 12, borderLeft: `3px solid ${t.color}` }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{t.desc}</div>
            </div>
          ))}
        </div>
        {allStrikes.length > 0 ? (
          <table style={styles.table}>
            <thead><tr><th style={styles.th}>Case</th><th style={styles.th}>Date</th><th style={styles.th}>Type</th><th style={styles.th}>Details</th></tr></thead>
            <tbody>
              {allStrikes.map((s: any, i: number) => (
                <tr key={i}>
                  <td style={styles.td}><span style={{ color: C.goldText, fontWeight: 600 }}>{s.caseId}</span> <span style={{ fontSize: 11, color: C.textMuted }}>{s.registrant}</span></td>
                  <td style={styles.td}>{fmtDate(s.date)}</td>
                  <td style={styles.td}><span style={{ ...styles.tag, background: C.dangerBg, color: C.danger }}>{s.type}</span></td>
                  <td style={styles.td}>{s.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div style={{ color: C.textMuted, fontSize: 13 }}>No infringer strikes recorded — clean record across all registrants</div>}
      </div>
    </div>
  );
}
